import {
  LexicalComposer,
  InitialConfigType,
} from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { CodeNode, CodeHighlightNode } from '@lexical/code';
import { MarkNode } from '@lexical/mark';
import { TableNode, TableRowNode, TableCellNode } from '@lexical/table';
import { $convertFromMarkdownString } from '@lexical/markdown';
import { LOAD_TRANSFORMERS } from './transformers';
import { theme } from './markdownEditorTheme';
import React from 'react';
import { LexicalEditor, ParagraphNode } from 'lexical';
import { AttachmentNode } from './customNodes/AttachmentNode/AttachmentNode';
import { DiffHunkNode } from './customNodes/DiffHunkNode/DiffHunkNode';
import { EditingPlugin } from './plugins/EditingPlugin';
import { AttachmentPlugin } from './plugins/AttachmentPlugin';
import { SavePlugin } from './plugins/SavePlugin';
import { StripCitationsPlugin } from './plugins/StripCitationsPlugin';
import { MarkdownChangePlugin } from './plugins/MarkdownChangePlugin';
import { PastedFilesPlugin } from './plugins/PastedFilesPlugin';
import { getSummaryForViewMode } from '../../Attachments/InlineAttachment';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { ToolbarPlugin } from './plugins/ToolbarPlugin';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { ListBreakPlugin } from './plugins/ListBreakPlugin';
import { NestedListPlugin } from './plugins/NestedListPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { MarkdownTablePlugin } from './plugins/MarkdownTablePlugin';
import { TableManipulationPlugin } from './plugins/TableManipulationPlugin';
import { DiffHunkPlugin } from './plugins/DiffHunkPlugin';
import { AllHunksResolvedPlugin } from './plugins/AllHunksResolvedPlugin';
import { InputGuardPlugin } from './plugins/InputGuardPlugin';
import { useImpressionEditorContext } from '../Context';
import { ErrorBoundary } from '../../../../../MarkdownEditor/Lexical/plugins/ErrorBoundary';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';

export const useStyles = makeStyles(() => ({
  editorContainer: {
    position: 'relative',
  },
  editor: {
    padding: '16px',
    minHeight: '150px',
    outline: 'none',
    lineHeight: 1.5,
    backgroundColor: 'transparent',
  },
  toolbar: {
    position: 'sticky',
    top: 26,
    paddingTop: '16px',
    paddingBottom: '16px',
    zIndex: 3,
    backgroundColor: '#fff',
  },
}));

export { theme } from './markdownEditorTheme';

export const LexicalStyle = () => {
  return (
    <style>{`
  .MarkdownEditorTheme__text-underline {
    text-decoration: underline;
  }
  .MarkdownEditorTheme__text-strikethrough {
    text-decoration: line-through;
  }
  .MarkdownEditorTheme__nested-list-item {
    list-style-type: none;
  }
  .MarkdownEditorTheme__nested-list-item:before,
  .MarkdownEditorTheme__nested-list-item:after {
    display: none;
  }
  .MarkdownEditorTheme__nested-list-item ol {
    list-style-type: lower-alpha;
  }
  .MarkdownEditorTheme__nested-list-item .MarkdownEditorTheme__nested-list-item ol {
    list-style-type: lower-roman;
  }
  .MarkdownEditorTheme__nested-list-item .MarkdownEditorTheme__nested-list-item .MarkdownEditorTheme__nested-list-item ol {
    list-style-type: disc;
  }
  .MarkdownEditorTheme__table {
    border-collapse: collapse;
    border-spacing: 0;
    width: 100%;
    margin: 16px 0;
    overflow-x: auto;
    display: table;
  }
  .MarkdownEditorTheme__tableRow {
    border-top: 1px solid #c6cbd1;
  }
  .MarkdownEditorTheme__tableCell,
  .MarkdownEditorTheme__tableCellHeader {
    border: 1px solid #d0d7de;
    padding: 6px 13px;
    text-align: left;
    vertical-align: middle;
    background-color: #fff;
  }
  .MarkdownEditorTheme__tableCellHeader {
    background-color: #f6f8fa;
    font-weight: 600;
  }
  .MarkdownEditorTheme__hr {
    border: none;
    border-top: 1px solid #d0d7de;
    margin: 16px 0;
    padding: 0;
  }
  .MarkdownEditorTheme__hrSelected {
    outline: 2px solid rgb(60, 132, 244);
  }
  .MarkdownEditorTheme__mark {
    cursor: pointer;
  }
  .MarkdownEditor__annotationAnchor {
    position: relative;
  }
`}</style>
  );
};

export type MarkdownEditorProviderProps = {
  onError: (error: Error, editor: LexicalEditor) => void;
  namespace: string;
  children: React.ReactNode;
};

const EditorProvider: React.FC<MarkdownEditorProviderProps> = ({
  children,
  onError,
  namespace,
}) => {
  const { isEditing, activeImpression, viewMode, canAnnotate } =
    useImpressionEditorContext();

  const editorContent =
    activeImpression?.reviewDiffDocument ?? activeImpression?.summary ?? '';

  const initialConfig: InitialConfigType = {
    namespace,
    onError,
    editorState: () =>
      $convertFromMarkdownString(
        getSummaryForViewMode(editorContent, viewMode),
        LOAD_TRANSFORMERS,
      ),
    nodes: [
      ParagraphNode,
      HeadingNode,
      QuoteNode,
      HorizontalRuleNode,
      ListNode,
      ListItemNode,
      LinkNode,
      CodeNode,
      CodeHighlightNode,
      AttachmentNode,
      DiffHunkNode,
      MarkNode,
      TableNode,
      TableRowNode,
      TableCellNode,
    ],
    editable:
      (isEditing || !!canAnnotate) && !activeImpression?.reviewDiffDocument,
    theme,
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <LexicalStyle />
      {children}
    </LexicalComposer>
  );
};

type EditorProps = {
  onPastedFiles: (files: File[]) => void;
  onAllDiffsResolved?: (markdown: string) => void;
};

const Editor: React.FC<EditorProps> = ({
  onPastedFiles,
  onAllDiffsResolved,
}) => {
  const { isEditing, activeImpression, canAnnotate } =
    useImpressionEditorContext();
  const classes = useStyles();
  const hasDiffDocument = Boolean(activeImpression?.reviewDiffDocument);
  const hideCaret = canAnnotate && !isEditing;

  return (
    <Box direction="column" fullWidth fullHeight>
      <ToolbarPlugin className={classes.toolbar} isEditing={isEditing} />
      <RichTextPlugin
        contentEditable={
          <ContentEditable
            className={classes.editor}
            style={hideCaret ? { caretColor: 'transparent' } : undefined}
          />
        }
        ErrorBoundary={ErrorBoundary}
      />
      <AttachmentPlugin />
      <HistoryPlugin />
      <EditingPlugin />
      <InputGuardPlugin />
      <SavePlugin />
      <StripCitationsPlugin />
      <MarkdownChangePlugin />
      <PastedFilesPlugin onPastedFiles={onPastedFiles} />
      <ListBreakPlugin />
      <NestedListPlugin />
      <TablePlugin
      /* for rendering the tables as Lexical Tables */
      />
      <MarkdownTablePlugin
      /* For converting a markdown tables to lexical tables */
      />
      <TableManipulationPlugin
      /* For adding/removing table rows and columns */
      />
      {hasDiffDocument && <DiffHunkPlugin />}
      {hasDiffDocument && onAllDiffsResolved && (
        <AllHunksResolvedPlugin onAllDiffsResolved={onAllDiffsResolved} />
      )}
    </Box>
  );
};

export const MarkdownEditor = {
  Provider: EditorProvider,
  Component: Editor,
};
